import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTenantContext } from '@/shared/providers/TenantProvider';
import { usePageTitleOverride } from '@/shared/providers/PageTitleProvider';
import { SEGMENT_MAP, SINGULAR_MAP, isDynamicId } from '@/shared/constants/pageTitleSegments';

/**
 * usePageTitle
 *
 * Automatically derives a descriptive browser tab title from the current URL
 * and sets `document.title` on every route change.
 *
 * Title format:
 *   - List pages:   "Trips - Ecolane Dispatch"
 *   - Detail pages: "Trip 12345 - Ecolane Dispatch"
 *   - Sub-pages:    "Schedule Versions - Ecolane Dispatch"
 *   - Dashboard:    "Dashboard - Ecolane Dispatch"
 *
 * The app name suffix is sourced from the tenant's `branding.appName` config,
 * falling back to "Ecolane Dispatch" before the tenant config has loaded.
 *
 * @param systemName - The `general.system.name` K3 config value, supplied by the
 *   caller (AppLayout reads it via useRuntimeConfig). Kept out of this hook so it
 *   stays thin and unit-testable without mocking the runtime-config/auth/api stack.
 * @returns The computed page label (the part before the " - <appName>" suffix),
 *          useful for testing or rendering in <title> tags directly.
 */
export const usePageTitle = (systemName?: string): string => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { tenant } = useTenantContext();

  // ECOPD-17017 — the tab-title suffix comes from the `general.system.name` K3
  // config value (passed in), falling back to the tenant branding appName, then
  // a constant.
  const appName: string =
    (typeof systemName === 'string' && systemName.trim()) ||
    tenant?.branding?.appName ||
    'Ecolane Dispatch';

  // ECOPD-17017 — a detail page may override the URL-derived label with the loaded
  // entity's name (e.g. "VET1"). Legacy shows "<Type> - <Name> - <system>" (e.g.
  // "Run - VET1 - Ecolane Dispatch"), so prepend the URL-derived singular entity
  // type to the name. Falls back to the path-derived label when there is no override.
  const override = usePageTitleOverride();
  let pageLabel: string;
  if (override) {
    const entityType = deriveEntityType(pathname, t);
    pageLabel = entityType ? `${entityType} ${override}` : override;
  } else {
    pageLabel = derivePageLabel(pathname, t);
  }
  const fullTitle = pageLabel ? `${pageLabel} - ${appName}` : appName;

  useEffect(() => {
    document.title = fullTitle;
  }, [fullTitle]);

  return pageLabel;
};

/**
 * Pure function that derives a human-readable page label from a pathname.
 * Exported so it can be unit-tested independently of the React hook.
 *
 * Algorithm (right-to-left segment scan):
 *
 * 1. Split pathname into non-empty segments.
 * 2. Strip trailing sub-page qualifiers that add no entity context
 *    (history, edit, versions, details, events, stops, return, rebook, copy).
 * 3. If the rightmost meaningful segment is a numeric/UUID ID:
 *    a. Look up the segment immediately before the ID in SINGULAR_MAP.
 *    b. If found → "Entity ID" (e.g. "Trip 12345").
 *    c. If not found → fall back to the segment label from SEGMENT_MAP.
 * 4. Otherwise use the rightmost meaningful segment mapped via SEGMENT_MAP.
 * 5. If nothing maps, return the raw segment capitalised.
 */
export const derivePageLabel = (
  pathname: string,
  t: (key: string, fallback: string) => string,
): string => {
  const segments = pathname.replace(/^\//, '').split('/').filter(Boolean);

  if (segments.length === 0) return '';

  // Sub-page qualifiers that should be stripped when building the title
  // (we keep their parent entity as the primary identifier).
  const TRAILING_QUALIFIERS = new Set([
    'history', 'edit', 'versions', 'details', 'events',
    'stops', 'return', 'rebook', 'copy', 'print-manifest',
    'service-rules', 'funding-eligibility',
  ]);

  // Walk backwards through segments, skipping trailing qualifiers first.
  // We keep track of qualifiers that were stripped so we can append them
  // when there is no ID present (e.g. "/operations/schedules/versions" → "Schedule versions").
  const strippedQualifiers: string[] = [];
  let tail = segments.length - 1;

  while (tail > 0 && TRAILING_QUALIFIERS.has(segments[tail].toLowerCase())) {
    strippedQualifiers.unshift(segments[tail].toLowerCase());
    tail--;
  }

  const tailSegment = segments[tail];
  const tailLower = tailSegment.toLowerCase();

  // ── Case 1: Tail is a dynamic ID ──────────────────────────────────────────
  if (isDynamicId(tailSegment)) {
    const parentSegment = tail > 0 ? segments[tail - 1].toLowerCase() : '';
    const singular = SINGULAR_MAP[parentSegment];

    if (singular) {
      const singularLabel = t(singular.i18nKey, singular.fallback);
      return `${singularLabel} ${tailSegment}`;
    }

    // Parent segment not in SINGULAR_MAP — fall back to parent label
    const parentMapping = SEGMENT_MAP[parentSegment];
    if (parentMapping) {
      return t(parentMapping.i18nKey, parentMapping.fallback);
    }

    // Nothing maps — return raw ID
    return tailSegment;
  }

  // ── Case 2: Tail is a known segment ──────────────────────────────────────
  const mapping = SEGMENT_MAP[tailLower];
  if (mapping) {
    const label = t(mapping.i18nKey, mapping.fallback);

    // If qualifiers were stripped AND tail is not a nav section itself,
    // append the first qualifier for extra context
    // e.g. /operations/schedules/:id/versions → tail after strip = schedule ID
    // (that case is handled above). Here we handle cases like
    // /operations/archive-trips/reconcile where "reconcile" stays as-is.
    return label;
  }

  // ── Case 3: Unrecognised segment — capitalise raw value ──────────────────
  return tailSegment.charAt(0).toUpperCase() + tailSegment.slice(1);
};

/**
 * Derives the singular entity TYPE label for a detail page (e.g. "Run", "Vehicle",
 * "Client") from the pathname, using the segment before the rightmost dynamic id.
 * Returns '' when the path is not an entity detail page or the parent isn't mapped.
 * Used to build the legacy "<Type> - <Name> - <system>" tab title. (ECOPD-17017)
 */
export const deriveEntityType = (
  pathname: string,
  t: (key: string, fallback: string) => string,
): string => {
  const segments = pathname.replace(/^\//, '').split('/').filter(Boolean);

  // Scan right-to-left for the rightmost dynamic id; its preceding segment names
  // the entity (handles trailing sub-pages like /runs/123/edit too).
  for (let i = segments.length - 1; i > 0; i--) {
    if (isDynamicId(segments[i])) {
      const parent = segments[i - 1].toLowerCase();
      const singular = SINGULAR_MAP[parent];
      return singular ? t(singular.i18nKey, singular.fallback) : '';
    }
  }

  return '';
};

export default usePageTitle;